import { isSignupInsurerNotListed } from '@bariendo/lib/insurance-prefill';
import { answerStrings, PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { PN_INSURER_NOT_LISTED, PN_INSURANCE_PREFILLED_ID } from '@bariendo/lib/pn-consultation-script';
import { PacWidget } from '@bariendo/lib/patient-access-script';
import { Select, Text } from '@mantine/core';
import { useCallback, useEffect } from 'react';
import { InsurerOption, useInsurerOptions } from './useInsurerOptions';
import { Row } from './PatientAccessCallWidgets';
import { useAnswerWidth } from './pacLayout';

interface InsurerSelectWidgetProps<T extends PatientAccessCallAnswers> {
  widget: Extract<PacWidget, { kind: 'insurerSelect' }>;
  answers: T;
  updateAnswers: (mutate: (current: T) => T) => void;
  /** The patient's state, whose insurer list to offer; every state's when unknown. */
  state: string | null | undefined;
  /** The state lookup hasn't settled: the list waits for it. */
  stateLoading?: boolean;
  /** The insurer's name from signup, picked once the list loads if nothing is chosen yet. */
  signupName?: string;
  readOnly?: boolean;
}

/**
 * The insurers for the patient's state (BAR-2919), from `useInsurerOptions`.
 * Records the name, the Organization reference (for the Coverage
 * write, BAR-2922) and the payer key (Cigna's lower BMI bars, and the network
 * lookup).
 */
export function InsurerSelectWidget<T extends PatientAccessCallAnswers>({
  widget,
  answers,
  updateAnswers,
  state,
  stateLoading,
  signupName,
  readOnly,
}: InsurerSelectWidgetProps<T>) {
  const { options, loading, failed } = useInsurerOptions(state, { enabled: !readOnly, stateLoading });
  const answerWidth = useAnswerWidth();

  const stored = typeof answers.values[widget.id] === 'string' ? (answers.values[widget.id] as string) : '';

  const choose = useCallback(
    (option: InsurerOption | undefined) =>
      updateAnswers((current) => ({
        ...current,
        values: {
          ...current.values,
          [widget.id]: option?.name,
          [`${widget.id}Reference`]: option?.reference,
          [`${widget.id}Key`]: option?.key,
        },
      })),
    [updateAnswers, widget.id]
  );
  /** "Mine isn't listed": the name alone, no Organization or payer key. */
  const chooseNotListed = useCallback(
    () =>
      updateAnswers((current) => ({
        ...current,
        values: {
          ...current.values,
          [widget.id]: PN_INSURER_NOT_LISTED,
          [`${widget.id}Reference`]: undefined,
          [`${widget.id}Key`]: undefined,
        },
      })),
    [updateAnswers, widget.id]
  );

  // The form recorded a name but not which Organization: match it once the list
  // is in. Once per note, through the card's prefilled marker, so a navigator
  // who clears the insurer is not overruled when the section reopens.
  useEffect(() => {
    if (readOnly || !signupName) return;
    // Signup's "Mine isn't listed" prefills the same choice where it's offered (v2.0, BAR-2998).
    const notListed = Boolean(widget.notListedOption) && isSignupInsurerNotListed(signupName);
    const match = notListed
      ? { name: PN_INSURER_NOT_LISTED, reference: undefined, key: undefined }
      : options.find((option) => option.name === signupName);
    if (!match) return;
    updateAnswers((current) => {
      const prefilled = answerStrings(current.values[PN_INSURANCE_PREFILLED_ID]);
      if (prefilled.includes(widget.id)) return current;
      const values: PatientAccessCallAnswers['values'] = {
        ...current.values,
        [PN_INSURANCE_PREFILLED_ID]: [...prefilled, widget.id],
      };
      if (values[widget.id] === undefined) {
        values[widget.id] = match.name;
        values[`${widget.id}Reference`] = match.reference;
        values[`${widget.id}Key`] = match.key;
      }
      return { ...current, values };
    });
  }, [readOnly, signupName, options, updateAnswers, widget.id, widget.notListedOption]);

  // A stored insurer the list no longer offers still needs an entry, or the select shows empty.
  const data = options.map((option) => option.name);
  // v2.0 ends the list as signup's picker does (BAR-2998).
  if (widget.notListedOption) data.push(PN_INSURER_NOT_LISTED);
  if (stored && !data.includes(stored)) data.push(stored);

  return (
    <Row label={widget.label} say={widget.say}>
      {readOnly ? (
        <Text size="sm" fw={stored ? 500 : 400} c={stored ? undefined : 'dimmed'} ta="right">
          {stored || '—'}
        </Text>
      ) : (
        <Select
          size="xs"
          w={answerWidth}
          clearable
          // A dropdown on v2.0, no typeahead (BAR-2998).
          searchable={!widget.notListedOption}
          aria-label={widget.label}
          disabled={loading && options.length === 0}
          placeholder={
            failed
              ? 'Couldn’t load insurers'
              : loading || stateLoading
                ? 'Loading insurers…'
                : options.length === 0
                  ? 'No insurers found'
                  : undefined
          }
          error={failed || undefined}
          data={data}
          value={stored || null}
          onChange={(name) =>
            widget.notListedOption && name === PN_INSURER_NOT_LISTED
              ? chooseNotListed()
              : choose(options.find((option) => option.name === name))
          }
        />
      )}
    </Row>
  );
}
